/**
 * Invoice-detail store - S5, the detail of one invoice (SIH-17 I-8c, TL A3).
 *
 * There is no per-invoice endpoint (ADR-005 v1.6 3.4 keeps the branch invoice
 * list for the worklists), so the invoice is found in that list: the branch's
 * invoices for the URL's period are read page by page, with no status filter,
 * until the row with this record number appears or the list ends. A 400 on an
 * advanced cursor is the end of the list (published 15), the same as the
 * directory; a 400 on the first page is an error.
 *
 * What this store never does (PD-33): no timer, no polling, no focus or
 * visibility re-read. A read happens because the screen opened, the reader
 * pressed Refresh, or the reader pressed Retry. Every answer whose request was
 * aborted (superseded or cancelled) is discarded, so a stale answer never
 * reads as current.
 */

import { create } from 'zustand'
import { fetchInvoices } from '@api/dashboardApi'
import type { InvoiceDetail } from '@domain/invoiceDetail'
import { ApiError, type InvoicePage, type InvoiceRow, type ViewState } from '@domain/types'

interface DetailScope {
  readonly dbName: string
  readonly from: string
  readonly to: string
  readonly rectNo: number
}

interface InvoiceDetailState {
  readonly view: ViewState<InvoiceDetail>
  readonly scope: DetailScope | null
  /** Open one invoice for a period (a new scope: shows loading, never an older invoice). */
  open: (dbName: string, period: { readonly from: string; readonly to: string }, rectNo: number) => void
  /** Explicit refresh: re-reads this branch's invoices only, keeping the row on screen. */
  refresh: () => void
  /** Re-reads from the first page after a failed read. */
  retry: () => void
  /** Cancel whatever is in flight; later answers are discarded. */
  cancel: () => void
}

/** The same page size the worklists read. */
const PAGE_SIZE = 200

let controller: AbortController | null = null

function begin(): AbortSignal {
  controller?.abort()
  controller = new AbortController()
  return controller.signal
}

function failure(error: unknown): string {
  return error instanceof ApiError
    ? error.message
    : `Cannot reach the dashboard service — ${(error as Error).message}`
}

/**
 * Walk the branch's invoice list one page at a time until the row appears or the list
 * ends. Recursive on purpose: each step is one awaited page, so there is no loop whose
 * exit condition a fault could remove (which would spin instead of failing).
 */
async function find(scope: DetailScope, signal: AbortSignal, cursor?: string): Promise<InvoiceRow | null> {
  let page: InvoicePage
  try {
    page = await fetchInvoices(scope.dbName, { from: scope.from, to: scope.to, cursor, limit: PAGE_SIZE }, signal)
  } catch (error) {
    // Published 15: a 400 on an advanced cursor is the end of the list.
    if (cursor !== undefined && error instanceof ApiError && error.status === 400) return null
    throw error
  }
  const found = page.invoices.find((row) => row.rectNo === scope.rectNo)
  if (found !== undefined) return found
  if (!page.hasMore || page.nextCursor === null) return null
  return find(scope, signal, page.nextCursor)
}

function previousOf(view: ViewState<InvoiceDetail>): InvoiceDetail | null {
  if (view.kind === 'ready') return view.data
  return view.kind === 'refreshing' ? view.previous : null
}

export const useInvoiceDetailStore = create<InvoiceDetailState>((set, get) => {
  async function read(scope: DetailScope, previous: InvoiceDetail | null): Promise<void> {
    const signal = begin()
    set({ scope, view: previous === null ? { kind: 'loading' } : { kind: 'refreshing', previous } })
    try {
      const row = await find(scope, signal)
      if (signal.aborted) return
      set({ view: { kind: 'ready', data: { row } } })
    } catch (error) {
      if (signal.aborted) return
      set({ view: { kind: 'error', message: failure(error) } })
    }
  }

  return {
    view: { kind: 'idle' },
    scope: null,

    open: (dbName, period, rectNo) => {
      void read({ dbName, from: period.from, to: period.to, rectNo }, null)
    },

    refresh: () => {
      const { scope, view } = get()
      if (scope === null) return
      void read(scope, previousOf(view))
    },

    retry: () => {
      const { scope } = get()
      if (scope === null) return
      void read(scope, null)
    },

    cancel: () => {
      controller?.abort()
      controller = null
    },
  }
})
